import { memo } from 'react';
import { Tag } from 'antd';
import type {
  CandidateQuestion,
  InterviewGuide as InterviewGuideRecord,
} from '../../../../../fsm/jobs';
import styles from './index.module.css';

export interface InterviewGuideProps {
  /** avrio-rio's hiring-manager guide, written at completion. Null or absent before then. */
  guide?: InterviewGuideRecord | null;
  /** The candidate's own questions to Rio, with Rio's answer or the flag for the recruiter. */
  questions?: CandidateQuestion[] | null;
}

export const hasInterviewGuideContent = (
  guide?: InterviewGuideRecord | null,
  questions?: CandidateQuestion[] | null,
): boolean =>
  Boolean(
    guide?.dimensions?.length ||
    guide?.skills?.length ||
    guide?.note ||
    questions?.length,
  );

/**
 * The hiring manager's interview guide (Rio PRD 5.7): questions for the real
 * interview, each mapped to something Rio's interview left open, ordered by
 * how much the gap weighs in the fit score. Rio writes it at completion
 * (avrio-rio, agents/interview/hm_guide.py); this only lays it out.
 *
 * "Ask first" repeats the guide's top three so a hiring manager who reads
 * nothing else still leaves with them (PRD 3.2: scannable in under two
 * minutes). Each dimension carries its reason, so a question is never a
 * generic competency question: the reader can see what the interview did not
 * establish and why this question is the one to ask.
 *
 * The candidate's own questions come last. The flagged ones are the promises
 * Rio made on the recruiter's behalf ("I'll flag that for the recruiter to
 * follow up with you"), so they are marked as the recruiter's to keep.
 */
export const InterviewGuide = memo(
  ({ guide, questions }: InterviewGuideProps) => {
    if (!hasInterviewGuideContent(guide, questions)) return null;
    const dimensions = guide?.dimensions ?? [];
    const skills = guide?.skills ?? [];
    const top = guide?.top ?? [];
    const asked = questions ?? [];
    const flagged = asked.filter((question) => question.flagged);
    const answered = asked.filter((question) => !question.flagged);
    if (!dimensions.length && !skills.length && !asked.length) {
      return <div className={styles.note}>{guide?.note}</div>;
    }
    return (
      <div className={styles.guide}>
        {top.length > 0 && (
          <section className={styles.block} aria-label="Ask first">
            <div className={styles.blockTitle}>Ask first</div>
            <ol className={styles.topList}>
              {top.map((question, index) => (
                <li key={`${index}-${question}`} className={styles.topItem}>
                  {question}
                </li>
              ))}
            </ol>
          </section>
        )}
        {dimensions.map((dimension, index) => (
          <section
            key={`${index}-${dimension.dimension}`}
            className={styles.block}
            aria-label={dimension.label ?? undefined}
          >
            <div className={styles.blockTitle}>{dimension.label}</div>
            {dimension.why && <div className={styles.why}>{dimension.why}</div>}
            <ol className={styles.questions}>
              {(dimension.questions ?? []).map((question, questionIndex) => (
                <li
                  key={`${questionIndex}-${question.question}`}
                  className={styles.questionItem}
                >
                  <div className={styles.question}>{question.question}</div>
                  {question.listenFor && (
                    <div className={styles.listenFor}>
                      Listen for: {question.listenFor}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </section>
        ))}
        {skills.length > 0 && (
          <section
            className={styles.block}
            aria-label="Required skills to confirm"
          >
            <div className={styles.blockTitle}>Required skills to confirm</div>
            {skills.map((skill, index) => (
              <div key={`${index}-${skill.skill}`} className={styles.skill}>
                <div className={styles.skillName}>{skill.skill}</div>
                {skill.why && <div className={styles.why}>{skill.why}</div>}
                {skill.question && (
                  <div className={styles.question}>{skill.question}</div>
                )}
                {skill.listenFor && (
                  <div className={styles.listenFor}>
                    Listen for: {skill.listenFor}
                  </div>
                )}
              </div>
            ))}
          </section>
        )}
        {asked.length > 0 && (
          <section className={styles.block} aria-label="The candidate asked">
            <div className={styles.blockTitle}>The candidate asked</div>
            <ul className={styles.askedList}>
              {flagged.map((question, index) => (
                <li
                  key={`flagged-${index}-${question.question}`}
                  className={styles.askedItem}
                >
                  <Tag className={styles.flag}>Yours to follow up</Tag>
                  <div className={styles.question}>{question.question}</div>
                </li>
              ))}
              {answered.map((question, index) => (
                <li
                  key={`answered-${index}-${question.question}`}
                  className={styles.askedItem}
                >
                  <div className={styles.question}>{question.question}</div>
                  {question.answer && (
                    <div className={styles.answer}>Rio: {question.answer}</div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    );
  },
);

InterviewGuide.displayName = 'InterviewGuide';

export default InterviewGuide;
